import React, { useMemo, useState, useCallback, useEffect } from 'react'
import InputField from 'components/styledFormElements/InputField'
import { Loader } from 'components/loader'
import {
  useGetUserAffinity,
  useGetUserHistory,
  useGetPersonalizedData,
} from 'hooks/personalization'
import { CustomTable } from 'components/customTable'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useForm, useWatch } from 'react-hook-form'
import PersonalizationTab from 'components/PersonalizedSimulation/PersonalizationTab'
import moment from 'moment'

type TabKey = 'history' | 'affinity' | 'personalization'
type PaginationState = { pageIndex?: number; pageSize?: number }

const PersonalizedSimulation = () => {
  const [mobileNumber, setMobileNumber] = useState<string>('')
  const [searchedMobileNumber, setSearchedMobileNumber] = useState<string>('')
  const [activeTab, setActiveTab] = useState<TabKey>('history')
  const [error, setError] = useState<string | null>(null)
  const historyRowSelectionState = useState<RowSelectionState>({})
  const affinityRowSelectionState = useState<RowSelectionState>({})
  const historyFilterFormHook = useForm<any>({
    defaultValues: {
      pagination: { pageIndex: 0, pageSize: 10 },
      search: '',
      sort: null,
      fields: [],
    },
  })
  const affinityFilterFormHook = useForm<any>({
    defaultValues: {
      pagination: { pageIndex: 0, pageSize: 10 },
      search: '',
      sort: null,
      fields: [],
    },
  })
  const historyPagination = useWatch({
    control: historyFilterFormHook.control,
    name: 'pagination',
  }) as PaginationState | undefined
  const affinityPagination = useWatch({
    control: affinityFilterFormHook.control,
    name: 'pagination',
  }) as PaginationState | undefined

  const {
    data: historyResponse,
    refetch: refetchHistory,
    isFetching: isHistoryLoading,
  } = useGetUserHistory(searchedMobileNumber, {
    enabled: false,
  })

  const {
    data: affinityResponse,
    refetch: refetchAffinity,
    isFetching: isAffinityLoading,
  } = useGetUserAffinity(searchedMobileNumber, {
    enabled: false,
  })

  const {
    data: personalizationResponse,
    mutateAsync: fetchPersonalization,
    isLoading: isPersonalizationLoading,
  } = useGetPersonalizedData()

  const isLoading =
    isHistoryLoading || isAffinityLoading || isPersonalizationLoading

  const historyData = useMemo(() => {
    const response = historyResponse?.data
    if (Array.isArray(response)) {
      return response
    }
    if (Array.isArray(response?.items)) {
      return response.items
    }
    if (Array.isArray(response?.data)) {
      return response.data
    }
    if (Array.isArray(response?.history)) {
      return response.history
    }
    return []
  }, [historyResponse])

  const affinityData = useMemo(() => {
    const response = affinityResponse?.data
    if (Array.isArray(response?.labels)) {
      return response.labels
    }
    if (Array.isArray(response)) {
      return response
    }
    if (Array.isArray(response?.data)) {
      return response.data
    }
    if (Array.isArray(response?.affinity)) {
      return response.affinity
    }
    if (response && typeof response === 'object') {
      return [response]
    }
    return []
  }, [affinityResponse])

  const historyTableData = useMemo(() => {
    return historyData.map((row: any) => ({
      episodeName: row?.episodeName ?? '-',
      createdAt: row?.createdAt ?? '-',
    }))
  }, [historyData])

  const affinityTableData = useMemo(() => {
    return affinityData.map((row: any) => ({
      label: row?.label ?? '-',
      score: row?.score ?? '-',
    }))
  }, [affinityData])

  const historyPageSize = historyPagination?.pageSize ?? 10
  const historyPageIndex = historyPagination?.pageIndex ?? 0
  const affinityPageSize = affinityPagination?.pageSize ?? 10
  const affinityPageIndex = affinityPagination?.pageIndex ?? 0

  const historyPages = useMemo(
    () => Math.max(1, Math.ceil(historyTableData.length / historyPageSize)),
    [historyPageSize, historyTableData.length]
  )
  const affinityPages = useMemo(
    () => Math.max(1, Math.ceil(affinityTableData.length / affinityPageSize)),
    [affinityPageSize, affinityTableData.length]
  )

  const paginatedHistoryData = useMemo(() => {
    const startIndex = historyPageIndex * historyPageSize
    return historyTableData.slice(startIndex, startIndex + historyPageSize)
  }, [historyPageIndex, historyPageSize, historyTableData])

  const paginatedAffinityData = useMemo(() => {
    const startIndex = affinityPageIndex * affinityPageSize
    return affinityTableData.slice(startIndex, startIndex + affinityPageSize)
  }, [affinityPageIndex, affinityPageSize, affinityTableData])

  useEffect(() => {
    const maxHistoryPageIndex = Math.max(0, historyPages - 1)
    if (historyPageIndex > maxHistoryPageIndex) {
      historyFilterFormHook.setValue('pagination', {
        pageIndex: maxHistoryPageIndex,
        pageSize: historyPageSize,
      })
    }
  }, [historyFilterFormHook, historyPageIndex, historyPageSize, historyPages])

  useEffect(() => {
    const maxAffinityPageIndex = Math.max(0, affinityPages - 1)
    if (affinityPageIndex > maxAffinityPageIndex) {
      affinityFilterFormHook.setValue('pagination', {
        pageIndex: maxAffinityPageIndex,
        pageSize: affinityPageSize,
      })
    }
  }, [affinityFilterFormHook, affinityPageIndex, affinityPageSize, affinityPages])

  const historyColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'episodeName',
        header: 'Episode Name',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue() as string | number | Date | null
          return value ? moment(value).format('LL') : '-'
        },
      },
    ],
    []
  )

  const affinityColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Label',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      },
      {
        accessorKey: 'score',
        header: 'Score',
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue<number>()
          return value !== undefined && value !== null
            ? Number(value).toFixed(2)
            : '-'
        },
      },
    ],
    []
  )

  const validateMobileNumber = (value: string) => {
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      setError('Please enter a mobile number')
      return false
    }
    if (!/^\d{10}$/.test(trimmedValue)) {
      setError('Please enter a valid mobile number')
      return false
    }
    return true
  }

  const getErrorMessageFromResult = (result: any) => {
    const apiError = result?.error
    return (
      apiError?.response?.data?.message ||
      apiError?.response?.data?.error ||
      apiError?.message ||
      'Something went wrong while fetching user data'
    )
  }

  const fetchByActiveTab = useCallback(async () => {
    if (activeTab === 'history') {
      return refetchHistory()
    }
    if (activeTab === 'affinity') {
      return refetchAffinity()
    }
    return fetchPersonalization({
      data: {
        userId: searchedMobileNumber,
      },
    })
  }, [activeTab, fetchPersonalization, refetchAffinity, refetchHistory, searchedMobileNumber])

  const handleSearch = useCallback(async () => {
    const trimmedMobileNumber = mobileNumber.trim()
    if (!validateMobileNumber(trimmedMobileNumber)) {
      return
    }

    setError(null)
    if (trimmedMobileNumber === searchedMobileNumber) {
      try {
        const result =
          activeTab === 'personalization'
            ? await fetchPersonalization({ data: { userId: trimmedMobileNumber } })
            : await fetchByActiveTab()
        if ((result as any)?.error) {
          setError(getErrorMessageFromResult(result))
        }
      } catch (apiError: any) {
        setError(apiError?.message || 'Something went wrong while fetching user data')
      }
      return
    }
    setSearchedMobileNumber(trimmedMobileNumber)
  }, [activeTab, fetchByActiveTab, fetchPersonalization, mobileNumber, searchedMobileNumber])

  useEffect(() => {
    if (!searchedMobileNumber) {
      return
    }
    fetchByActiveTab().then((result: any) => {
      if (result?.error) {
        setError(getErrorMessageFromResult(result))
      }
    })
  }, [activeTab, searchedMobileNumber, fetchByActiveTab])

  const handleMobileNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMobileNumber(e.target.value)
      // Clear error when user starts typing
      if (error) {
        setError(null)
      }
    },
    [error]
  )

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearch()
      }
    },
    [handleSearch]
  )

  const tabs = useMemo(
    () => [
      { key: 'history' as TabKey, label: 'History' },
      { key: 'affinity' as TabKey, label: 'User Affinity' },
      { key: 'personalization' as TabKey, label: 'Personalization' },
    ],
    []
  )

  return (
    <div className="w-full px-6 py-6 bg-gray-50 dark:bg-slate-900">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
          Personalized Simulation
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Search by mobile number to fetch user history and affinity details
        </p>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-slate-700 pb-3">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setError(null)
                    setActiveTab(tab.key)
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${isActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-200'
                    }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1 w-full">
              <InputField
                label="Mobile Number"
                type="tel"
                value={mobileNumber}
                helpText="Enter the mobile number to search for user history and affinity"
                onChange={handleMobileNumberChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter mobile number"
                maxLength={10}
                error={error || undefined}
                containerClassName="flex flex-col"
              />
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={isLoading || !mobileNumber.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap h-[42px]"
            >
              {isLoading ? (
                <>
                  <Loader />
                  <span>Searching...</span>
                </>
              ) : (
                <span>Search / Fetch</span>
              )}
            </button>
          </div>

          {error && (
            <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {searchedMobileNumber && !error && (
            <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Showing results for: <span className="font-medium">{searchedMobileNumber}</span>
              </p>
            </div>
          )}

          {!isLoading && searchedMobileNumber && (
            <div className="grid grid-cols-1 gap-6 mt-4">
              {activeTab === 'history' && (
                <div className="border border-gray-200 dark:border-slate-700 rounded-md p-4">
                  {historyTableData.length > 0 ? (
                    <CustomTable
                      columns={historyColumns}
                      data={paginatedHistoryData}
                      pages={historyPages}
                      totalRecords={historyTableData.length}
                      rowSelectionState={historyRowSelectionState}
                      isLoading={isLoading}
                      formHook={historyFilterFormHook}
                      title="History"
                      searchVisible={false}
                      useFieldForSelect="episodeName"
                    />
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No user history found.
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'affinity' && (
                <div className="border border-gray-200 dark:border-slate-700 rounded-md p-4">
                  {affinityTableData.length > 0 ? (
                    <CustomTable
                      columns={affinityColumns}
                      data={paginatedAffinityData}
                      pages={affinityPages}
                      totalRecords={affinityTableData.length}
                      rowSelectionState={affinityRowSelectionState}
                      isLoading={isLoading}
                      formHook={affinityFilterFormHook}
                      title="Affinity"
                      searchVisible={false}
                      useFieldForSelect="label"
                    />
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No user affinity found.
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'personalization' && (
                <div className="border border-gray-200 dark:border-slate-700 rounded-md p-4">
                  <PersonalizationTab
                    data={personalizationResponse?.data}
                    isLoading={isLoading}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PersonalizedSimulation